import { useState, useMemo } from "react";
import Fuse from "fuse.js";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  Pagination,
  Button,
  Chip,
  Stack,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import useGhostPosts, { GhostPost } from "../../../hooks/useGhostPosts";
import { Link as RouterLink } from "react-router-dom";
import Seo from "../../../seo/Seo";

function readingTimeFromHtml(html = "") {
  const text = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text ? text.split(" ").length : 0;
  const minutes = Math.max(1, Math.round(words / 200));
  return { words, minutes };
}

function htmlToExcerpt(html = "", max = 220) {
  const text = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
}

export default function Blog() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));

  const { posts, loading, error } = useGhostPosts(100);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState<string | null>(null);
  const [sort, setSort] = useState<"new" | "old">("new");

  const postsPerPage = 6;

  const allTags = useMemo(() => {
    const s = new Set<string>();
    posts.forEach((p: any) => p.tags?.forEach((t: any) => s.add(t.name)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const fuse = useMemo(
    () =>
      new Fuse(posts, {
        keys: [
          { name: "title", weight: 0.7 },
          { name: "html", weight: 0.3 },
        ],
        threshold: 0.38,
        ignoreLocation: true,
      }),
    [posts]
  );

  const filteredSorted = useMemo(() => {
    const base = tag
      ? posts.filter((p: any) => p.tags?.some((t: any) => t.name === tag))
      : posts;
    const searched = search.trim()
      ? fuse.search(search).map((r) => r.item)
      : base;
    const dedup = [...new Set(searched)];
    const sorted = [...dedup].sort((a: GhostPost, b: GhostPost) => {
      const da = new Date(a.published_at).getTime();
      const db = new Date(b.published_at).getTime();
      return sort === "new" ? db - da : da - db;
    });
    return sorted;
  }, [posts, tag, search, fuse, sort]);

  const totalPages = Math.ceil(filteredSorted.length / postsPerPage);
  const currentPosts = useMemo(() => {
    const start = (page - 1) * postsPerPage;
    return filteredSorted.slice(start, start + postsPerPage);
  }, [filteredSorted, page]);

  const isFeaturedMode =
    page === 1 && !search.trim() && !tag && currentPosts.length > 0;
  const featured = isFeaturedMode ? currentPosts[0] : null;
  const gridPosts = isFeaturedMode ? currentPosts.slice(1) : currentPosts;

  const hasFilters = !!search.trim() || !!tag;
  const path = `/blog${page > 1 ? `?page=${page}` : ""}`;

  return (
    <Box
      component="section"
      sx={{
        background: `linear-gradient(180deg, ${alpha(
          theme.palette.primary.light,
          0.08
        )} 10%, ${alpha(theme.palette.background.paper, 0.28)} 40%)`,
        py: { xs: 4, md: 10 },
      }}
    >
      <Seo
        title="Blog — news"
        description="Useful materials about bondpay "
        path={path}
        type="website"
        noindex={hasFilters}
      />

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 } }}>
        <Stack spacing={2.5} alignItems="stretch" sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant={isXs ? "h5" : "h3"}
            component="h1"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              backgroundImage: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              color: "transparent",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              mb: { xs: 1.5, md: 0 },
            }}
          >
            Our Blog
          </Typography>

          <Paper
            elevation={3}
            sx={{
              alignSelf: "center",
              mx: "auto",
              width: "100%",
              maxWidth: { xs: "100%", sm: 560, md: 720 },
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <TextField
              fullWidth
              size={isXs ? "small" : "medium"}
              variant="outlined"
              placeholder="Search posts by title or content…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 1.5 }}>
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { "& fieldset": { border: "none" }, px: 1 },
              }}
            />
          </Paper>

          {allTags.length > 0 && (
            <Box
              sx={{
                width: "100%",
                py: 1,
                overflowX: { xs: "visible", md: "auto" },
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha(theme.palette.primary.main, 0.3),
                  borderRadius: 999,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  rowGap: 1,
                  minWidth: "max-content",
                }}
              >
                <Chip
                  label="All"
                  onClick={() => {
                    setTag(null);
                    setPage(1);
                  }}
                  color={!tag ? "secondary" : "default"}
                  variant={!tag ? "filled" : "outlined"}
                  size={isXs ? "small" : "medium"}
                />
                {allTags.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => {
                      setTag((prev) => (prev === t ? null : t));
                      setPage(1);
                    }}
                    color={tag === t ? "secondary" : "default"}
                    variant={tag === t ? "filled" : "outlined"}
                    size={isXs ? "small" : "medium"}
                    sx={{ backdropFilter: "blur(8px)" }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <ToggleButtonGroup
            exclusive
            value={sort}
            onChange={(_, v) => v && setSort(v)}
            size={isXs ? "small" : "medium"}
            sx={{
              alignSelf: "center",
              borderRadius: 999,
              p: 0.5,
            }}
          >
            <ToggleButton value="new">Newest</ToggleButton>
            <ToggleButton value="old">Oldest</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading && (
          <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item key={i} xs={12} sm={6} md={6} lg={4}>
                <Skeleton
                  variant="rectangular"
                  height={isXs ? 160 : 240}
                  sx={{ borderRadius: 3, mb: 2 }}
                />
                <Skeleton variant="text" height={isXs ? 28 : 36} />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="60%" />
              </Grid>
            ))}
          </Grid>
        )}

        {error && !loading && (
          <Typography
            variant="body1"
            sx={{ color: "error.main", textAlign: "center", mb: 4 }}
          >
            Error: {error.message}
          </Typography>
        )}
        {!loading && !error && filteredSorted.length === 0 && (
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              fontStyle: "italic",
              mb: 4,
            }}
          >
            No posts match your filters. Try clearing search or tags.
          </Typography>
        )}

        {featured && (
          <Card
            sx={{
              mb: { xs: 4, md: 6 },
              p: 1,
              borderRadius: 4,
              border: "1px solid transparent",
              boxShadow: "0 8px 28px rgba(0,0,0,.18)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: { xs: 280, sm: 380, md: 520 },
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 0px 4px rgba(0,0,0,.3)",
                mx: 1,
                mt: 1,
              }}
            >
              {featured.feature_image ? (
                <CardMedia
                  component="img"
                  image={featured.feature_image}
                  alt={featured.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    backgroundColor: "background.paper",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    bgcolor: "background.paper",
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,

                  background: `linear-gradient(180deg, ${alpha(
                    theme.palette.primary.main,
                    0.0
                  )} 0%, rgba(0,0,0,.0) 40%, rgba(0,0,0,.7) 100%)`,
                }}
              />

              <Stack
                spacing={1}
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: 12,
                  right: 12,
                  color: "common.white",
                  px: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  rowGap={0.75}
                >
                  {featured.tags?.slice(0, 4).map((t: any) => (
                    <Chip
                      key={t.slug}
                      label={t.name}
                      size="small"
                      sx={{
                        bgcolor: alpha("#FFFFFF", 0.2),
                        color: "#fff",
                        backdropFilter: "blur(6px)",
                        border: `1px solid ${alpha("#fff", 0.3)}`,
                      }}
                    />
                  ))}
                </Stack>

                <Typography
                  variant={isSm ? "h5" : "h4"}
                  sx={{
                    fontWeight: 900,
                    textShadow: "0 2px 16px rgba(0,0,0,.45)",
                  }}
                >
                  {featured.title}
                </Typography>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {new Date(featured.published_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    • {readingTimeFromHtml(featured.html).minutes} min read
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size={isXs ? "small" : "medium"}
                    component={RouterLink}
                    to={`/article/${encodeURIComponent(featured.slug)}`}
                  >
                    Read more
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <CardContent sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {htmlToExcerpt(featured.html, isXs ? 140 : 240)}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
          {gridPosts.map((post: GhostPost) => {
            const rt = readingTimeFromHtml(post.html);
            return (
              <Grid item key={post.id} xs={12} sm={6} md={6} lg={4}>
                <Card
                  sx={{
                    p: { xs: 0.75, md: 1 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    background: `linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) border-box`,
                    border: "1px solid transparent",
                    boxShadow: "0 8px 24px rgba(0,0,0,.10)",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 36px rgba(0,0,0,.16)",
                    },
                  }}
                >
                  {post.feature_image && (
                    <Box
                      sx={{
                        position: "relative",
                        borderRadius: 2,
                        overflow: "hidden",
                        mx: 1,
                        mt: 1,
                        height: { xs: 160, sm: 200, md: 220 },
                        bgcolor: "background.paper",
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={post.feature_image}
                        alt={post.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {post.tags?.[0]?.name && (
                        <Chip
                          label={post.tags[0].name}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            bgcolor: alpha(theme.palette.secondary.light, 0.85),
                            border: `1px solid ${alpha(
                              theme.palette.secondary.dark,
                              0.35
                            )}`,
                          }}
                        />
                      )}
                    </Box>
                  )}

                  <CardContent sx={{ flex: 1 }}>
                    <Typography
                      variant={isXs ? "h6" : "h5"}
                      sx={{ fontWeight: 800, mb: 0.75 }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 2 }}
                    >
                      {htmlToExcerpt(post.html, isXs ? 140 : 220)}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mt: "auto" }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        component={RouterLink}
                        to={`/article/${encodeURIComponent(post.slug)}`}
                        title="Read more"
                      >
                        Read more
                      </Button>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {new Date(post.published_at).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                        {" • "}
                        {rt.minutes} min
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {totalPages > 1 && (
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setPage(value);
              }}
              color="secondary"
              shape="rounded"
              size={isXs ? "small" : "medium"}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
